import { memo } from "react";
import Page from "components/page/Page";
import { Helmet } from "react-helmet";
import Card from "components/card/Card";
import { PageCollapseWidth } from "../../constants";
import GoogleAd from "components/googleAd/GoogleAd";
import { Formik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "hooks";
import emailSlice from "reducers/emailSlice";
import { Form, Spinner } from "react-bootstrap";
import { styled } from "styled-components";
import TextInput from "components/textInput/TextInput";
import Error from "components/error/Error";
import { RootState } from "store";
import {
  sendContactErrorSelector,
  sendContactSuccessSelector,
  sendingContactSelector,
} from "selectors/emailSelectors";
import PrimaryButton from "components/primaryButton/PrimaryButton";

const InputTextArea = styled(TextInput)`
  height: 200px;
`;

const ContactPage = () => {
  const dispatch = useAppDispatch();

  const sendingContact = useAppSelector((state: RootState) =>
    sendingContactSelector(state),
  );

  const sendContactError = useAppSelector((state: RootState) =>
    sendContactErrorSelector(state),
  );

  const sendContactSuccess = useAppSelector((state: RootState) =>
    sendContactSuccessSelector(state),
  );

  const schema = Yup.object({
    email: Yup.string()
      .email("A valid email is required")
      .required("Email is a required field"),
    message: Yup.string().required("Message is a required field"),
  });

  const handleSubmit = (values: any) => {
    dispatch(emailSlice.actions.sendContact(values));
  };

  return (
    <>
      <Helmet>
        <title>Contact ToolWeaver</title>
        <meta
          name="description"
          content="Contact ToolWeaver for questions, feedback, or support regarding our free online tools."
        />
        <link rel="canonical" href="https://toolweaver.com/contact" />
      </Helmet>
      <Page>
        <GoogleAd hideOnWidth={PageCollapseWidth} />
        <Card collapseOnWidth={PageCollapseWidth} title="Contact ToolWeaver">
          <>
            <span className="mb-4 d-block">
              If you want to get in contact with us for any reason please use
              the form below.
            </span>
            <Formik
              validationSchema={schema}
              onSubmit={handleSubmit}
              initialValues={{
                email: "",
                message: "",
              }}
            >
              {(props) => (
                <Form onSubmit={props.handleSubmit} autoComplete="off">
                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>Email address</Form.Label>
                    <TextInput
                      disabled={sendContactSuccess}
                      readOnly={sendContactSuccess}
                      id="email"
                      spellCheck={false}
                      className="form-control"
                      hasError={
                        props.touched.email && props.errors.email !== undefined
                      }
                      value={props.values.email}
                      onChange={props.handleChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-4" controlId="message">
                    <Form.Label>Message</Form.Label>
                    <InputTextArea
                      readOnly={sendContactSuccess}
                      disabled={sendContactSuccess}
                      id="message"
                      className="form-control"
                      textarea={true}
                      hasError={
                        props.touched.message &&
                        props.errors.message !== undefined
                      }
                      value={props.values.message}
                      onChange={props.handleChange}
                    />
                  </Form.Group>

                  <Error text={sendContactError} />

                  <PrimaryButton
                    disabled={sendingContact || sendContactSuccess}
                  >
                    {!sendingContact && (
                      <span>
                        {sendContactSuccess ? "Message Sent" : "Send"}
                      </span>
                    )}
                    {sendingContact && <Spinner size="sm" />}
                  </PrimaryButton>
                </Form>
              )}
            </Formik>
          </>
        </Card>
      </Page>
    </>
  );
};

export default memo(ContactPage);
